import { collection, onSnapshot } from "firebase/firestore";
import { useEffect, useRef } from "react";
import { setReportsData } from "../Redux/reports/action";
import { setErrorStatus } from "../Redux/status/action";
import { database } from "../Services/firebase";
import { isValidArray } from "../Services/validators";
import {
  getLastDateOfMonth,
  getTimestampFromDateStr,
  monthStrToInt
} from "../Utils/Constants/Constants";
import { reportsTableField } from "../Utils/Constants/Reports";

export function useReports({ isAuth, filterData, sort }) {
  const reportsListener = useRef(null);
  const reports = useRef(null);

  const sortReports = (data, sortConfig) => {
    return data?.sort((a, b) => {
      let fieldA, fieldB;

      switch (sortConfig?.title) {
        case "PERIOD":
          fieldA = a?.info?.period?.from;
          fieldB = b?.info?.period?.from;
          break;
        case "UPLOADED BY":
          fieldA = a?.uploadedBy.email;
          fieldB = b?.uploadedBy.email;
          break;
        default:
          fieldA = a?.[reportsTableField(sortConfig?.title, "sort")];
          fieldB = b?.[reportsTableField(sortConfig?.title, "sort")];
          break;
      }

      if (["PERIOD", "UPLOADED AT"].includes(sort.title)) {
        return sort.ascending
          ? fieldA
              ?.toString()
              ?.localeCompare(fieldB, undefined, { numeric: true })
          : fieldB
              ?.toString()
              ?.localeCompare(fieldA, undefined, { numeric: true });
      } else {
        const stringA = String(fieldA).toLowerCase();
        const stringB = String(fieldB).toLowerCase();
        if (stringA < stringB) {
          return sortConfig.ascending ? -1 : 1;
        }
        if (stringA > stringB) {
          return sortConfig.ascending ? 1 : -1;
        }
      }

      return 0;
    });
  };

  const filterReports = (filterData, reports) => {
    if (!reports) {
      return [];
    }

    const month = isValidArray(filterData["month"])
      ? String(monthStrToInt(filterData["month"][0]) + 1).padStart(2, "0")
      : null;

    const year = isValidArray(filterData["year"])
      ? filterData["year"][0]
      : null;

    return Object.values(reports).filter((item) => {
      const createdAt = new Date(item.info.period.from);

      // Filter by reportType
      if (isValidArray(filterData["reportType"])) {
        const filterTypes = filterData["reportType"].map((type) =>
          String(type)
            .toLowerCase()
            .trim()
        );

        const itemType = String(item?.type || "")
          .toLowerCase()
          .trim();

        if (!filterTypes.includes(itemType)) {
          console.warn("Skipped due to type:", itemType);
          return false;
        }
      }
      // Filter by month + year
      if (month && year) {
        const startTime = getTimestampFromDateStr(`01/${month}/${year}`);
        const endTime = getTimestampFromDateStr(
          `${getLastDateOfMonth(year, month)}/${month}/${year}`, true
        );

        if (createdAt < startTime || createdAt > endTime) {
          return false;
        }
      }

      // Filter by only year
      else if (year && !month) {
        const startTime = getTimestampFromDateStr(`01/01/${year}`);
        const endTime = getTimestampFromDateStr(`31/12/${year}`, true);
        if (createdAt < startTime || createdAt > endTime) {
          return false;
        }
      }

      return true;
    });
  };

  useEffect(() => {
    const docPeriod = getTimePeriodFromFilter(filterData);
    const kpiReport = {
      name: `KPI_Summary_${timestampToDate(docPeriod?.from)}-${timestampToDate(
        docPeriod?.to
      )}.xlsx`,
      type: "KPI Summary",
      createdAt: docPeriod?.to,
      documentId: "kpiReport",
      info: {
        period: docPeriod
      },
      uploadedBy: {
        email: "N/A",
        name: "N/A"
      }
    };

    const filteredData = filterReports(filterData, {
      ...reports.current,
      ...(docPeriod.from < +new Date()
        ? { [kpiReport.documentId]: kpiReport }
        : {})
    });
    const sortedData = sortReports(filteredData, sort);

    const reportsData = {};
    sortedData?.forEach((data) => {
      reportsData[data.documentId] = data;
    });

    setReportsData(reportsData);
    // eslint-disable-next-line
  }, [filterData, sort, reports.current]);

  const subscribeToReports = () => {
    const collectionRef = collection(database, "files");

    return onSnapshot(
      collectionRef,
      (dataSnapshot) => {
        const reportsData = {};
        dataSnapshot.forEach((doc) => {
          if (!doc.data().actions?.deletedAt) {
            reportsData[doc.id] = {
              ...doc.data(),
              documentId: doc.id
            };
          }
        });
        reports.current = reportsData;
      },
      (error) => {
        console.error(error, "from reports listener");
        setErrorStatus(error);
      }
    );
  };

  useEffect(() => {
    if (isAuth === true && reportsListener.current === null) {
      reportsListener.current = subscribeToReports();
    } else if (
      isAuth === false &&
      typeof reportsListener.current === "function"
    ) {
      reportsListener.current();
      reportsListener.current = null;
    }
    // eslint-disable-next-line
  }, [isAuth]);
}

function getTimePeriodFromFilter(filterData) {
  const month = isValidArray(filterData["month"])
    ? String(monthStrToInt(filterData["month"][0]) + 1).padStart(2, "0")
    : null;

  const year = isValidArray(filterData["year"]) ? filterData["year"][0] : null;

  if (month && year) {
    const startTime = getTimestampFromDateStr(`01/${month}/${year}`);
    const endTime = getTimestampFromDateStr(
      `${getLastDateOfMonth(year, month)}/${month}/${year}`, true
    );
    return {
      from: startTime,
      to: endTime > +new Date() ? +new Date() : endTime
    };
  }

  // Filter by only year
  else if (year && !month) {
    const startTime = getTimestampFromDateStr(`01/01/${year}`);
    const endTime = getTimestampFromDateStr(`31/12/${year}`, true);
    return {
      from: startTime,
      to: endTime > +new Date() ? +new Date() : endTime
    };
  }
}

function timestampToDate(timestamp) {
  const date = new Date(timestamp);

  const day = date.getDate();
  const month = date.toLocaleString("en-US", { month: "short" }).toUpperCase();
  const year = date.getFullYear();

  return `${day}_${month}_${year}`;
}
